import React, { useRef, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import PanelAboveDashboard from './PanelAboveDashboard/PanelAboveDashboard';
import { useDashboardData } from './useDashboardData';
import { useGridConfiguration } from './useGridConfiguration';
import { useGridHandlers } from './useGridHandlers';
import { useInfiniteScrollHandler } from './useInfiniteScrollHandler';
import { useDeselectOnFilterChange } from './useDeselectOnFilterChange';
import { useSideBarConfiguration } from './useSideBarConfiguration';
import StatusButtons from './StatusButtons/StatusButtons';
import { statusButtonsVisibleAtom } from './atoms';
import styles from './Dashboard.module.scss';

export const styled: React.CSSProperties = {
  fontFamily: 'Fira Sans,Helvetica Neue,Helvetica,Roboto,Arial,sans-serif',
};

const NewDashboard = () => {
  const gridReference = useRef<AgGridReact>(null);
  const isStatusButtonsVisible = useAtomValue(statusButtonsVisibleAtom);

  const {
    dashboardData,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useDashboardData();

  const allJobs = useMemo(() => dashboardData?.jobs || [], [dashboardData]);

  const {
    columnDefinitions,
    defaultColumnDefinition,
    autoGroupColumnDefinition,
    rowSelection,
    getRowId,
    selectionColumnDefinition,
  } = useGridConfiguration();

  const { gridApiReference, onGridReady, onSelectionChanged } =
    useGridHandlers();

  const { onBodyScroll } = useInfiniteScrollHandler({
    fetchNextPage,
    gridApiReference,
    hasNextPage,
    isFetchingNextPage,
  });

  const { sideBarConfiguration } = useSideBarConfiguration();

  useDeselectOnFilterChange();

  if (isError) {
    return (
      <div className={styles.error}>
        Error loading dashboard: {error?.message}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Orchestration Status</h1>
        {isStatusButtonsVisible &&
          (isLoading ? (
            <div className={styles.skeletonButtons}>
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className={styles.skeletonButton}>
                  <div className={styles.skeletonCount} />
                  <div className={styles.skeletonContent}>
                    <div className={styles.skeletonIcon} />
                    <div className={styles.skeletonLabel} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <StatusButtons />
          ))}
      </div>
      <PanelAboveDashboard />
      <div className={styles.gridContainer}>
        <AgGridReact
          theme={themeAlpine.withParams(styled)}
          ref={gridReference}
          rowData={allJobs}
          columnDefs={columnDefinitions}
          defaultColDef={defaultColumnDefinition}
          autoGroupColumnDef={autoGroupColumnDefinition}
          selectionColumnDef={selectionColumnDefinition}
          treeData={true}
          treeDataChildrenField="children"
          getDataPath={(data) => [data.jobId.toString()]}
          getRowId={getRowId}
          rowSelection={rowSelection}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onBodyScroll={onBodyScroll}
          cellSelection={true}
          loading={isLoading || isFetchingNextPage}
          groupDefaultExpanded={0}
          suppressAggFuncInHeader={true}
          enableFilterHandlers={true}
          pivotPanelShow={'onlyWhenPivoting'}
          sideBar={sideBarConfiguration}
        />
      </div>
    </div>
  );
};

export default NewDashboard;
